import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AdminBlogTag } from '@app/model/blog-management.model';
import { BlogManagementDataService } from '@app/services/blog-management-data.service';
import { Subject, Subscription } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material';
import { BlogTagFormComponent } from '../blog-tag-form/blog-tag-form.component';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-blog-tag-management',
  templateUrl: './blog-tag-management.component.html',
  styleUrls: ['./blog-tag-management.component.scss']
})
export class BlogTagManagementComponent implements OnInit, OnDestroy {
  @ViewChild('pageNumberInput', { static: false })
  pageNumberInput: ElementRef<HTMLInputElement>;

  tags: AdminBlogTag[] = [];
  filteredTags: AdminBlogTag[] = [];

  searchText = '';

  pageIndex = 1;
  pageSize = 10;
  isEditingPage = false;

  isLoading = false;
  errorMessage = '';
  deletingTagId = '';

  private destroy$ = new Subject<void>();
  private tagsSubscription = new Subscription();

  constructor(
    private blogManagementDataService: BlogManagementDataService,
    private dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadTags();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.tagsSubscription.unsubscribe();
  }

  get pagedTags(): AdminBlogTag[] {
    const startIndex = (this.pageIndex - 1) * this.pageSize;

    return this.filteredTags.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredTags.length / this.pageSize));
  }

  get visiblePages(): number[] {
    const maxVisiblePages = 5;

    let startPage = Math.max(1, this.pageIndex - Math.floor(maxVisiblePages / 2));

    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    startPage = Math.max(1, endPage - maxVisiblePages + 1);

    const pages: number[] = [];

    for (let page = startPage; page <= endPage; page++) {
      pages.push(page);
    }

    return pages;
  }

  loadTags(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.tagsSubscription.unsubscribe();

    this.tagsSubscription = this.blogManagementDataService
      .getTags()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (tags) => {
          this.tags = tags;
          this.isLoading = false;
          this.applyFilter();
        },
        (error) => {
          console.error('Load blog tags failed:', error);

          this.tags = [];
          this.filteredTags = [];
          this.isLoading = false;
          this.errorMessage = 'Không thể tải danh sách thẻ';
        }
      );
  }

  applyFilter(): void {
    const keyword = this.searchText.trim().toLowerCase();

    this.filteredTags = this.tags.filter((tag) => {
      return (
        !keyword ||
        tag.name.toLowerCase().includes(keyword) ||
        tag.slug.toLowerCase().includes(keyword)
      );
    });

    this.pageIndex = 1;
    this.isEditingPage = false;
  }

  clearSearch(): void {
    this.searchText = '';
    this.applyFilter();
  }

  goToPage(page: number): void {
    if (!Number.isInteger(page) || page < 1 || page > this.totalPages) {
      return;
    }

    this.pageIndex = page;
    this.isEditingPage = false;
  }

  goToEnteredPage(value: string): void {
    const page = Number(value);

    if (Number.isInteger(page) && page >= 1 && page <= this.totalPages) {
      this.goToPage(page);
      return;
    }

    this.cancelPageEdit();
  }

  startPageEdit(): void {
    this.isEditingPage = true;

    setTimeout(() => {
      if (this.pageNumberInput) {
        this.pageNumberInput.nativeElement.focus();
        this.pageNumberInput.nativeElement.select();
      }
    });
  }

  cancelPageEdit(): void {
    this.isEditingPage = false;
  }

  openCreateTag(): void {
    const dialogRef = this.dialog.open(BlogTagFormComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'blog-tag-form-dialog',
      data: {
        mode: 'create',
        tags: this.tags
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result.saved) {
          this.loadTags();
        }
      });
  }

  openEditTag(tag: AdminBlogTag): void {
    const dialogRef = this.dialog.open(BlogTagFormComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'blog-tag-form-dialog',
      data: {
        mode: 'edit',
        tags: this.tags,
        tag: tag
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result.saved) {
          this.loadTags();
        }
      });
  }

  confirmDeleteTag(tag: AdminBlogTag): void {
    if (this.deletingTagId) {
      return;
    }

    const title = 'Xóa thẻ';

    const description = 'Bạn có chắc chắn muốn xóa thẻ "' + tag.name + '" không?';

    const waitDescription = 'Đang xóa thẻ...';

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDescription);

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.deleteTag(tag);
      });
  }

  private deleteTag(tag: AdminBlogTag): void {
    this.deletingTagId = tag.id;

    this.blogManagementDataService
      .deleteTag(tag.id)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.deletingTagId = '';
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(
              response && response.message ? response.message : 'Không thể xóa thẻ'
            );

            return;
          }

          this.toastr.success('Xóa thẻ thành công');

          this.loadTags();
        },
        (error) => {
          console.error('Delete blog tag failed:', error);

          const errorResponse = error && error.error ? error.error : null;

          let message = 'Không thể xóa thẻ';

          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          } else if (error.status === 400 || error.status === 409) {
            message = 'Thẻ đang được sử dụng nên không thể xóa';
          }

          this.toastr.error(message);
        }
      );
  }
}
